import { Booking } from '../../models/Booking'
import { Estimate } from '../../models/Estimate'
import { Gift } from '../../models/Gift'
import { Invoice } from '../../models/Invoice'
import { Line } from '../../models/Line'
import { Order } from '../../models/Order'
import { Package } from '../../models/Package'
import { Payment } from '../../models/Payment'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { Subscription } from '../../models/Subscription'
import { Taxcode } from '../../models/Taxcode'
import { Transaction } from '../../models/Transaction'
import { User } from '../../models/User'
import { currencyFloat } from '../../utils/numbers'
import { AllowedTrandocTypes } from './ChargeWorkflow'

const INVOICE_TOTAL_LIMIT = 500000.0

type PaymentInitType = {
    canEditTotal: boolean
    canProceedWhenTotalIsZero: boolean
    minimumAmount: number | undefined
    maximumAmount: number | undefined
    type: 'amount' | 'amount_or_itemize' | undefined
    payment: Payment
    trandoc: Transaction | undefined
    viewRecordPathname: string
    recordTypeName: string | undefined
    canPayViaOther: boolean
    usesPaymentPlan: boolean
    taxable: boolean
}

function isTaxableFromOwner(owner: User) {
    return (
        Taxcode.defaultTaxable(owner) &&
        (owner.solo || owner.company || owner.perm >= User.PERM_ROLE_EMPLOYEE)
    )
}

function calculateMinimumPaymentAmount(trandoc: Invoice | Package) {
    return currencyFloat(
        trandoc.paidAmount && trandoc.paidAmount > 0 && trandoc.maxPaymentCount === 2
            ? trandoc.owedAmount
            : (((trandoc.total || 0) - (trandoc.discountAmount || 0)) *
                  (trandoc.minPaymentPercent || 0)) /
                  100
    )
}

function getAdjustedTaxForPaymentPlan(trandoc: Invoice | Package) {
    // when we are charging a payment plan, we want the taxable amount without the discounts
    const copy = trandoc.clone({
        lines: trandoc.lines.map((line) => line.clone({ discountable: false })),
    })
    return (copy.tax ?? 0) * ((copy.taxableAmount ?? 0) / (copy.total ?? 1))
}

function getModelFromRecordType(path: AllowedTrandocTypes) {
    switch (path) {
        case 'booking':
            return Booking
        case 'estimate':
            return Estimate
        case 'invoice':
            return Invoice
        case 'gift':
            return Gift
        case 'package':
            return Package
        case 'order':
            return Order
        case 'subscription':
            return Subscription
        default:
            return undefined
    }
}

const setLinesFromTransaction = (transaction: Transaction) => {
    const lines = transaction.lines
    let lineTotal = 0
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        if (line.total) lineTotal += line.total
    }
    if (lineTotal > 0) {
        const ratio = (transaction.total ?? 0) / lineTotal
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i]
            if (line.total) {
                const lineCopy = Line.initWithLine(line, transaction)
                lineCopy.total = line.total * ratio
                const quantity = line.quantity ? line.quantity : 1
                lineCopy.rate = line.rate ? line.total / quantity : line.total
                lines[i] = lineCopy
            }
        }
    }
    else {
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i]
            const lineCopy = Line.initWithLine(line, transaction)
            lineCopy.total = transaction.total! / lines.length
            const quantity = line.quantity ? line.quantity : 1
            lineCopy.rate = lineCopy.total / quantity
            lines[i] = lineCopy
        }
    }
}

// --- Helpers to initialize payments

const initializeChargePayment = (me: User, owner: User, client?: User, subaccount?: Subaccount) => {
    const payment = new Payment({
        type: 'charge',
        owner: owner,
        allow_surcharge: owner.allowSurcharge,
        memo: owner.receiptPolicy || 'Thank you for your business',
        client: client,
        vendor: owner,
        subaccount: subaccount,
        is_pos: true,
        employee: me.payee && me.perm == User.PERM_ROLE_EMPLOYEE ? me : undefined,
    })

    return {
        payment,
        canEditTotal: true,
        canProceedWhenTotalIsZero: false,
        minimumAmount: undefined,
        maximumAmount: undefined,
        type: undefined,
        trandoc: undefined,
        viewRecordPathname: client ? `/dashboard/user/${client.id}` : '/',
        previewRecordPathname: undefined,
        recordTypeName: undefined,
        canPayViaOther: true,
        usesPaymentPlan: false,
        taxable: isTaxableFromOwner(owner),
    }
}

const copyChargePayment = (payment: Payment, owner: User) => {
    return {
        payment,
        canEditTotal: true,
        canProceedWhenTotalIsZero: false,
        minimumAmount: undefined,
        maximumAmount: undefined,
        type: undefined,
        trandoc: undefined,
        viewRecordPathname: payment.client ? `/dashboard/user/${payment.client.id}` : '/',
        previewRecordPathname: undefined,
        recordTypeName: undefined,
        canPayViaOther: true,
        usesPaymentPlan: false,
        taxable: isTaxableFromOwner(owner),
    }
}

const initializeInvoicePayment = (invoice: Invoice): PaymentInitType => {
    if (invoice.allowAnyPayment) {
        const canEditTotal = (invoice.remainingPaymentCount || 0) > 1
        const payment = new Payment({
            type: 'invoice',
            client: invoice.client,
            owner: invoice.owner,
            vendor: invoice.owner,
            employee: invoice.employee,
            subaccount: invoice.subaccount,
            invoice: invoice,
            total: canEditTotal ? 0 : invoice.remainingBalance,
            booking: invoice.booking,
            tax: getAdjustedTaxForPaymentPlan(invoice),
            taxcode: invoice.taxcode,
            nexus: invoice.nexus,
            location: invoice.location,
            allow_surcharge: invoice.allowSurcharge,
            // lines: invoice.lines, // payment lines are not the same as invoice lines on a payment plan
            // discount: invoice.discount, // already included in the invoice, don't add again when charging
            // discount_type: invoice.discountType,
            is_pos: true,
            override_total: !!invoice.paidAmount,
            memo: invoice.memo,
        })

        return {
            payment,
            canEditTotal,
            canProceedWhenTotalIsZero: false,
            minimumAmount: calculateMinimumPaymentAmount(invoice),
            maximumAmount: invoice.owedAmount,
            type: 'amount',
            trandoc: invoice,
            viewRecordPathname: `/invoices/${invoice.id}`,
            recordTypeName: 'Invoice',
            canPayViaOther: (invoice.remainingPaymentCount ?? 0) === 1, // only show other payment options if there are multiple payments left
            usesPaymentPlan: true,
            taxable: Boolean(invoice.allowSalesTax),
        }
    }

    return {
        payment: new Payment({
            type: 'invoice',
            client: invoice.client,
            owner: invoice.owner,
            vendor: invoice.owner,
            invoice: invoice,
            total: invoice.total,
            booking: invoice.booking,
            tax: invoice.tax,
            taxcode: invoice.taxcode,
            nexus: invoice.nexus,
            location: invoice.location,
            allow_surcharge: invoice.allowSurcharge,
            lines: invoice.lines,
            discount: invoice.discount,
            discount_type: invoice.discountType,
            is_pos: true,
            memo: invoice.memo,
        }),
        canEditTotal: false,
        canProceedWhenTotalIsZero: false,
        minimumAmount: undefined,
        maximumAmount: undefined,
        type: undefined,
        trandoc: invoice,
        viewRecordPathname: `/invoices/${invoice.id}`,

        recordTypeName: 'Invoice',
        canPayViaOther: true,
        usesPaymentPlan: false,
        taxable: Boolean(invoice.allowSalesTax),
    }
}

const initializePackagePayment = (pkg: Package): PaymentInitType => {
    if (pkg.allowAnyPayment) {
        const canEditTotal = (pkg.remainingPaymentCount || 0) > 1
        const payment = new Payment({
            type: 'package',
            client: pkg.client,
            owner: pkg.owner,
            vendor: pkg.owner,
            employee: pkg.employee,
            subaccount: pkg.subaccount,
            a_package: pkg,
            package_ref: pkg,
            total: canEditTotal ? 0 : pkg.remainingBalance,
            // discount: pkg.discount, // already included in the package, don't add again when charging
            // discount_type: pkg.discountType,
            tax: getAdjustedTaxForPaymentPlan(pkg),
            taxcode: pkg.taxcode,
            nexus: pkg.nexus,
            location: pkg.location,
            allow_surcharge: pkg.allowSurcharge, // already included in the package, don't add again when charging
            // lines: pkg.lines, // payment lines are not the same as package lines on a payment plan
            is_pos: true,
            override_total: !!pkg.paidAmount,
            memo: pkg.memo,
        })

        return {
            payment,
            canEditTotal,
            canProceedWhenTotalIsZero: false,
            minimumAmount: calculateMinimumPaymentAmount(pkg),
            maximumAmount: pkg.owedAmount,
            type: 'amount',
            trandoc: pkg,
            viewRecordPathname: `/packages/${pkg.id}`,
            recordTypeName: 'Package',
            canPayViaOther: (pkg.remainingPaymentCount ?? 0) === 1, // only show other payment options if there are multiple payments left
            usesPaymentPlan: true,
            taxable: Boolean(pkg.allowSalesTax),
        }
    }
    return {
        payment: new Payment({
            type: 'package',
            client: pkg.client,
            owner: pkg.owner,
            vendor: pkg.owner,
            a_package: pkg,
            package_ref: pkg,
            total: pkg.total,
            discount: pkg.discount,
            discount_type: pkg.discountType,
            tax: pkg.tax,
            taxcode: pkg.taxcode,
            nexus: pkg.nexus,
            location: pkg.location,
            allow_surcharge: !!pkg.allowSurcharge,
            lines: pkg.lines,
            is_pos: true,
            memo: pkg.memo,
        }),
        canEditTotal: false,
        canProceedWhenTotalIsZero: false,
        minimumAmount: undefined,
        maximumAmount: undefined,
        type: undefined,
        trandoc: pkg,
        viewRecordPathname: `/packages/${pkg.id}`,
        recordTypeName: 'Package',
        canPayViaOther: true,
        usesPaymentPlan: false,
        taxable: Boolean(pkg.allowSalesTax),
    }
}

const initializeGiftPayment = (gift: Gift): PaymentInitType => {
    return {
        payment: new Payment({
            type: 'gift',
            client: gift.client,
            owner: gift.owner,
            vendor: gift.owner,
            gift: gift,
            total: gift.total,
            tax: gift.tax,
            taxcode: gift.taxcode,
            nexus: gift.nexus,
            location: gift.location,
            discount: gift.discount,
            discount_type: gift.discountType,
            allow_surcharge: !!gift.allowSurcharge,
            lines: gift.lines,
            is_pos: true,
            memo: gift.memo,
        }),
        canEditTotal: false,
        canProceedWhenTotalIsZero: false,
        minimumAmount: undefined,
        maximumAmount: undefined,
        type: undefined,
        trandoc: gift,
        viewRecordPathname: `/gifts/${gift.id}`,
        recordTypeName: 'Gift certificate',
        canPayViaOther: true,
        usesPaymentPlan: false,
        taxable: Boolean(gift.allowSalesTax),
    }
}

const initializeBookingPayment = (
    owner: User,
    booking: Booking,
    subType?: string
): PaymentInitType => {
    const determineGratuity = (booking: Booking) => {
        if (!booking.gratuity) return { gratuity: undefined, gratuity_type: undefined }

        if (booking.paidDepositDate && booking.gratuityType === 'percent') {
            return {
                gratuity: (booking.gratuity * (booking.amount ?? 0)) / 100,
                gratuity_type: 'rate',
            }
        }
        return { gratuity: booking.gratuity, gratuity_type: booking.gratuityType }
    }

    const setLinesFromTransaction = (transaction: Transaction) => {
        const lines = transaction.lines
        let lineTotal = 0
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i]
            if (line.total) lineTotal += line.total
        }
        if (lineTotal > 0) {
            const ratio = (transaction.total ?? 0) / lineTotal
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i]
                if (line.total) {
                    const lineCopy = Line.initWithLine(line, transaction)
                    lineCopy.total = line.total * ratio
                    const quantity = line.quantity ? line.quantity : 1
                    lineCopy.rate = line.rate ? line.total / quantity : line.total
                    lines[i] = lineCopy
                }
            }
        }
        else {
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i]
                const lineCopy = Line.initWithLine(line, transaction)
                lineCopy.total = transaction.total! / lines.length
                const quantity = line.quantity ? line.quantity : 1
                lineCopy.rate = lineCopy.total / quantity
                lines[i] = lineCopy
            }
        }
    }

    if (subType === 'complete') {
        const payment = new Payment({
            type: 'complete',
            client: booking.client,
            owner: owner,
            vendor: owner,
            employee: booking.employee,
            subaccount: booking.subaccount,
            booking: booking,
            total: booking.total,
            tax: booking.tax,
            paid_deposit_tax: booking.paidDepositTax,
            taxcode: booking.taxcode,
            nexus: booking.nexus,
            location: booking.nexus === 'transaction' ? booking.getLocation() : undefined,
            discount: booking.discount,
            discount_type: booking.discountType,
            allow_surcharge: !!booking.allowSurcharge,
            paid_deposit: booking.paidDepositDate && booking.deposit ? booking.deposit : undefined,
            lines: booking.lines,
            is_pos: true,
            source: booking.source,
            ...determineGratuity(booking),
            memo: booking.memo,
        })

        const shouldProrate = booking.lines.length > 0 && payment.total !== booking.total
        if (booking.taxableAmount && shouldProrate) {
            setLinesFromTransaction(payment)
        }

        return {
            payment,
            canEditTotal: booking.rate !== 0, // if the booking total is 0, skip to the payment step
            canProceedWhenTotalIsZero: booking.rate === undefined || booking.rate === null, // also allow TBD
            minimumAmount: 0,
            maximumAmount: INVOICE_TOTAL_LIMIT,
            type: owner.allowProduct ? 'amount_or_itemize' : 'amount',
            trandoc: booking,
            viewRecordPathname: `/bookings/${booking.id}`,
            recordTypeName: 'Booking',
            canPayViaOther: true,
            usesPaymentPlan: false,
            taxable: Boolean(booking.allowSalesTax),
        }
    }

    if (subType === 'confirm') {
        const payment = new Payment({
            type: booking.confirmedDate ? 'complete' : 'deposit',
            total: booking.confirmedDate ? booking.owedAmount : booking.deposit,
            client: booking.client,
            owner: booking.owner,
            vendor: booking.owner,
            booking: booking,
            tax: booking.taxableAmount ? booking.tax : undefined,
            taxcode: booking.taxableAmount ? booking.taxcode : undefined,
            nexus: booking.taxableAmount ? booking.nexus : undefined,
            location:
                booking.taxableAmount && booking.nexus === 'transaction'
                    ? booking.getLocation()
                    : undefined,
            allow_surcharge: !!booking.allowSurcharge,
            is_pos: true,
            source: booking.source,
            memo: booking.memo,
            lines: booking.taxableAmount ? booking.lines : [],
        })

        const shouldProrate = booking.lines.length > 0 && payment.total !== booking.total
        if (booking.taxableAmount && shouldProrate) {
            setLinesFromTransaction(payment)
        }

        return {
            payment,
            canEditTotal: booking.rate === undefined || booking.rate === null,
            canProceedWhenTotalIsZero: false,
            minimumAmount: undefined,
            maximumAmount: undefined,
            type: undefined,
            trandoc: booking,
            viewRecordPathname: `/bookings/${booking.id}`,
            recordTypeName: 'Booking',
            canPayViaOther: !Boolean(booking.deposit),
            usesPaymentPlan: false,
            taxable: Boolean(booking.allowSalesTax),
        }
    }

    throw new Error(`Invalid subType: ${subType}`)
}

const initializeEstimatePayment = (estimate: Estimate): PaymentInitType => {
    if (estimate.confirmedDate) {
        return {
            payment: new Payment({
                type: 'complete',
                client: estimate.client,
                owner: estimate.owner,
                vendor: estimate.owner,
                employee: estimate.employee,
                estimate: estimate,
                total: estimate.total,
                tax: estimate.tax,
                paid_deposit_tax: estimate.paidDepositTax,
                taxcode: estimate.taxcode,
                nexus: estimate.nexus,
                location: estimate.location,
                discount: estimate.discount,
                discount_type: estimate.discountType,
                allow_surcharge: !!estimate.allowSurcharge,
                paid_deposit: estimate.deposit,
                lines: estimate.lines,
                gratuity: estimate.gratuity,
                gratuity_type: estimate.gratuityType,
                is_pos: true,
                memo: estimate.memo,
            }),
            canEditTotal: false,
            canProceedWhenTotalIsZero: false,
            minimumAmount: undefined,
            maximumAmount: undefined,
            type: undefined,
            trandoc: estimate,
            viewRecordPathname: `/estimates/${estimate.id}`,
            recordTypeName: 'Estimate',
            canPayViaOther: true,
            usesPaymentPlan: false,
            taxable: Boolean(estimate.allowSalesTax),
        }
    }

    const payment = new Payment({
        type: 'deposit',
        client: estimate.client,
        owner: estimate.owner,
        vendor: estimate.owner,
        estimate: estimate,
        total: estimate.deposit,
        lines: estimate.taxableAmount ? estimate.lines : [],
        tax: estimate.taxableAmount ? estimate.tax : undefined,
        taxcode: estimate.taxableAmount ? estimate.taxcode : undefined,
        nexus: estimate.taxableAmount ? estimate.nexus : undefined,
        location: estimate.taxableAmount ? estimate.location : undefined,
        allow_surcharge: !!estimate.allowSurcharge,
        source: estimate.source,
        is_pos: true,
        memo: estimate.memo,
    })

    const shouldProrate = estimate.lines.length > 0 && payment.total !== estimate.total
    if (estimate.taxableAmount && shouldProrate) {
        setLinesFromTransaction(payment)
    }

    return {
        payment,
        canEditTotal: false,
        canProceedWhenTotalIsZero: false,
        minimumAmount: undefined,
        maximumAmount: undefined,
        type: undefined,
        trandoc: estimate,
        viewRecordPathname: `/estimates/${estimate.id}`,
        recordTypeName: 'Estimate',
        canPayViaOther: !Boolean(estimate.deposit),
        usesPaymentPlan: false,
        taxable: Boolean(estimate.allowSalesTax),
    }
}

const initializeSubscriptionPayment = (subscription: Subscription): PaymentInitType => {
    return {
        payment: new Payment({
            type: 'subscription',
            client: subscription.client,
            owner: subscription.owner,
            vendor: subscription.owner,
            subscription: subscription,
            total: subscription.total,
            discount: subscription.discount,
            discount_type: subscription.discountType,
            tax: subscription.taxableAmount ? subscription.tax : undefined,
            taxcode: subscription.taxableAmount ? subscription.taxcode : undefined,
            nexus: subscription.taxableAmount ? subscription.nexus : undefined,
            location: subscription.taxableAmount ? subscription.location : undefined,
            allow_surcharge: !!subscription.allowSurcharge,
            source: subscription.source,
            is_pos: true,
            memo: subscription.memo,
        }),
        canEditTotal: false,
        canProceedWhenTotalIsZero: false,
        minimumAmount: undefined,
        maximumAmount: undefined,
        type: undefined,
        trandoc: subscription,
        viewRecordPathname: `/subscriptions/${subscription.id}`,
        recordTypeName: 'Subscription',
        canPayViaOther: false,
        usesPaymentPlan: false,
        taxable: Boolean(subscription.allowSalesTax),
    }
}

// --- Helpers to load records

const loadOrFetchInvoice = async (recordID: string) => {
    let invoice = await RecordFactory.getInstance().loadInvoice(recordID)
    if (!invoice) {
        if ((await RecordFactory.getInstance().fetchInvoice(recordID)).ok) {
            invoice = await RecordFactory.getInstance().loadInvoice(recordID)
        }
    }
    return invoice
}

const loadOrFetchPackage = async (recordID: string) => {
    let pkg = await RecordFactory.getInstance().loadPackage(recordID)
    if (!pkg) {
        if (
            (await RecordFactory.getInstance().fetchPackage(new Package({ id: recordID }), 'view'))
                .ok
        ) {
            pkg = await RecordFactory.getInstance().loadPackage(recordID)
        }
    }
    return pkg
}

const loadOrFetchGift = async (recordID: string) => {
    let gift = await RecordFactory.getInstance().loadGift(recordID)
    if (!gift) {
        if ((await RecordFactory.getInstance().fetchGift(recordID)).ok) {
            gift = await RecordFactory.getInstance().loadGift(recordID)
        }
    }
    return gift
}

const loadOrFetchBooking = async (recordID: string) => {
    // fetch booking doesn't seem to exist?
    return await RecordFactory.getInstance().loadBooking(recordID)
}

const loadOrFetchEstimate = async (recordID: string) => {
    let estimate = await RecordFactory.getInstance().loadEstimate(recordID)
    if (!estimate) {
        if ((await RecordFactory.getInstance().fetchEstimate(recordID)).ok) {
            estimate = await RecordFactory.getInstance().loadEstimate(recordID)
        }
    }
    return estimate
}

const loadOrFetchSubscription = async (recordID: string) => {
    // cannot fetch subscription here?
    return await RecordFactory.getInstance().loadSubscription(recordID)
}

// --- Helpers to initialize payments

export async function initializePayment(
    me: User,
    owner: User,
    recordType?: AllowedTrandocTypes,
    recordID?: string,
    subType?: string,
    client?: User,
    subaccount?: Subaccount,
    copyPaymentID?: string
): Promise<PaymentInitType & { taxable: boolean }> {
    if (copyPaymentID !== undefined) {
        const payment = await RecordFactory.getInstance().loadPayment(copyPaymentID)
        if (!payment) {
            throw new Error('Payment not found')
        }
        return copyChargePayment(payment, owner)
    }

    if (!recordType || !recordID) {
        return initializeChargePayment(me, owner, client, subaccount)
    }

    const model = getModelFromRecordType(recordType)
    if (!model) {
        throw new Error('Invalid record type')
    }

    switch (recordType) {
        case 'invoice':
            const invoice = await loadOrFetchInvoice(recordID)
            if (!invoice) {
                throw new Error('Invoice not found')
            }
            return initializeInvoicePayment(invoice)
        case 'package':
            const pkg = await loadOrFetchPackage(recordID)
            if (!pkg) {
                throw new Error('Package not found')
            }
            return initializePackagePayment(pkg)
        case 'gift':
            const gift = await loadOrFetchGift(recordID)
            if (!gift) {
                throw new Error('Gift not found')
            }
            return initializeGiftPayment(gift)
        case 'booking':
            const booking = await loadOrFetchBooking(recordID)
            if (!booking) {
                throw new Error('Booking not found')
            }
            return initializeBookingPayment(owner, booking, subType)
        case 'estimate':
            const estimate = await loadOrFetchEstimate(recordID)
            if (!estimate) {
                throw new Error('Estimate not found')
            }
            return initializeEstimatePayment(estimate)
        case 'subscription':
            const subscription = await loadOrFetchSubscription(recordID)
            if (!subscription) {
                throw new Error('Subscription not found')
            }
            return initializeSubscriptionPayment(subscription)
        default:
            return initializeChargePayment(me, owner, client)
    }
}
